import { useId, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { messages } from '@nesc/shared';
import { cn } from '@/lib/utils';

export type FieldProps = {
  label: string;
  required?: boolean;
  /** 入力欄の下に出す補助文 */
  hint?: ReactNode;
  error?: string;
  /** 文字数カウンタ。上限の 80% 超で太字にする */
  count?: { used: number; max: number };
  className?: string;
  children: (props: { id: string; 'aria-invalid': boolean; 'aria-describedby': string }) => ReactNode;
};

/**
 * フォーム項目（docs/09-design-system.md 第8章）。
 * 必須はラベル右に表示し、エラーは直下にアイコン付きで出す。
 */
export const Field = ({ label, required, hint, error, count, className, children }: FieldProps) => {
  const id = useId();
  const describedBy = `${id}-desc`;
  const overThreshold = count ? count.used > count.max * 0.8 : false;
  const over = count ? count.used > count.max : false;

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-body font-semibold text-foreground">
          {label}
          {required && (
            <span className="ml-1.5 text-note font-normal text-foreground-muted">
              {messages.common.required}
            </span>
          )}
        </label>
        {count && (
          <span
            className={cn(
              'tabular text-note text-foreground-subtle',
              overThreshold && 'font-semibold text-foreground-muted',
              over && 'font-semibold text-foreground',
            )}
          >
            {messages.message.charCount(count.used, count.max)}
          </span>
        )}
      </div>

      {children({ id, 'aria-invalid': !!error, 'aria-describedby': describedBy })}

      <div id={describedBy} className="space-y-1">
        {error && (
          <p className="flex items-start gap-1.5 text-note font-semibold text-foreground">
            <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0" />
            <span>{error}</span>
          </p>
        )}
        {hint && !error && <p className="text-note text-foreground-muted">{hint}</p>}
      </div>
    </div>
  );
};
